import React, { useState } from "react";
import axios from "axios";
import {
  Button,
  TextField,
  Card,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Box,
} from "@mui/material";
import { styled } from "@mui/system";
import "./LoanSearchPage.css";
import { fetchJsonDynamic } from "../../../data/DynamicServiceNew/jsondynamicservice";
import { loanManagementUrl } from "../../../config";

const formatDate = (date) => {
  if (!date) return "-";
  const d = new Date(date);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Styled components
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StatusTypography = styled(Typography)(({ status }) => ({
  color: status === "Cleared" ? "green" : "red",
  fontWeight: "bold",
}));

const LoanScheduleTable = ({ scheduleData, installmentAmount }) => {
  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold", color: "primary.main" }}>
              ID
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", color: "primary.main" }}>
              Installment Amount
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", color: "primary.main" }}>
              Due Date
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", color: "primary.main" }}>
              Details
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {scheduleData && scheduleData.length > 0 ? (
            scheduleData.map((schedule, index) => (
              <StyledTableRow key={index}>
                <TableCell>{schedule.Id}</TableCell>
                <TableCell>{installmentAmount}</TableCell>
                <TableCell>{formatDate(schedule.ExpectedDueDate)}</TableCell>
                <TableCell>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Amount to Pay</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Payment Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {["Principal", "Interest", "Penalty"].map((type) => {
                        const typeData = {
                          Principal: {
                            isPaid: schedule.IsPrinciplePaid,
                            amountToPay: schedule.PrincipalAmount,
                            paymentDate: schedule.PrinciplePaidDate,
                          },
                          Interest: {
                            isPaid: schedule.IsInterestPaid,
                            amountToPay: schedule.InterestAmount,
                            paymentDate: schedule.InterestPaidDate,
                          },
                          Penalty: {
                            isPaid: schedule.IsPenaltyPaid,
                            amountToPay: schedule.PenaltyAmount,
                            paymentDate: schedule.PenaltyPaidDate,
                          },
                        }[type];

                        const status = typeData.isPaid ? "Cleared" : "Pending";
                        return (
                          <StyledTableRow key={type}>
                            <TableCell>{type}</TableCell>
                            <TableCell>{typeData.amountToPay}</TableCell>
                            <TableCell>
                              <StatusTypography status={status}>
                                {status}
                              </StatusTypography>
                            </TableCell>
                            <TableCell>
                              {formatDate(typeData.paymentDate) || "-"}
                            </TableCell>
                          </StyledTableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableCell>
              </StyledTableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan="4" align="center">
                No loan schedules available.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const LoanSearchPage = () => {
  const [loanId, setLoanId] = useState("");
  const [loanDetails, setLoanDetails] = useState(null);
  const [loanSchedule, setLoanSchedule] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    if (!loanId) {
      setError("Please enter a Customer Number.");
      return;
    }
    try {
      const response = await fetchJsonDynamic(
        "allLoanSchedulesByLoanId",
        "parameters",
        {
          loanId: parseInt(loanId),
        },
        loanManagementUrl.uri,
      );

      const data = JSON.parse(
        response.results
      );

      if (data.LoanSchedules.length === 0) {
        setError(data.allLoanSchedulesByLoanId.error);
        setLoanDetails(null);
        setLoanSchedule([]);
        return;
      }

      setLoanDetails(data.LoanApplications[0]);
      setLoanSchedule(data.LoanSchedules);

      if (!data.LoanSchedules) {
        setError("No Loan Schedule found for the given Customer.");
        setLoanDetails(null);
        setLoanSchedule([]);
        return;
      }
    } catch (err) {
      setError("An error occurred while fetching loan Schedule data.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      className="container"
      sx={{ padding: 2, maxWidth: 900, margin: "auto" }}
    >
      <Card sx={{ padding: 3, marginTop: 4 }}>
        <Grid container spacing={2} justifyContent="center" alignItems="center">
          <Grid item xs={12}>
            <Typography variant="h4" color="primary">
              Loan Schedule
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="Enter Loan Id"
              value={loanId}
              onChange={(e) => setLoanId(e.target.value)}
              variant="outlined"
              error={!!error}
              helperText={error}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              fullWidth
              sx={{ textTransform: "none" }}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Card>

      {loading && (
        <Grid container justifyContent="center" sx={{ marginTop: 4 }}>
          <CircularProgress />
        </Grid>
      )}

      {loanDetails && (
        <Card sx={{ padding: 3, marginTop: 4, backgroundColor: "#f0f8ff" }}>
          <Typography variant="h5" color="secondary" gutterBottom>
            Loan Details
          </Typography>
          <Grid container spacing={2} sx={{ marginTop: 2 }}>
            {[
              { label: "Loan ID", value: loanDetails.Id },
              { label: "Loan Amount", value: loanDetails.LoanAmount },
              {
                label: "Principal Amount",
                value: loanDetails.PrincipalAmount ?? 0.0,
              },
              {
                label: "Borrow Date",
                value: formatDate(loanDetails.DateCreated),
              },
              {
                label: "Amount Disbursed",
                value: loanDetails.AmountToDisburse ?? 0.0,
              },
              { label: "Interest Amount", value: loanDetails.InterestAmount },
              {
                label: "Expected Clear Date",
                value: formatDate(loanDetails.ExpectedClearDate),
              },
              {
                label: "Loan Balance",
                value: loanDetails.AmountToPay - loanDetails.PrincipalAmount ?? 0.00,
              },
            ].map((detail, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Typography>
                  <strong>{detail.label}: </strong>
                  {detail.value}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Card>
      )}

      {loanSchedule && loanSchedule.length > 0 && (
        <Card sx={{ padding: 3, marginTop: 4 }}>
          <Typography variant="h5" color="secondary" gutterBottom>
            Loan Schedule
          </Typography>
          <LoanScheduleTable
            scheduleData={loanSchedule}
            installmentAmount={loanDetails.InstallmentAmount}
          />
        </Card>
      )}
    </Box>
  );
};

export default LoanSearchPage;
